// Cigar Education Data - Ring gauges, sizes, wrappers, terminology

export interface VitolaSizeInfo {
  name: string;
  length: string; // in inches
  ringGauge: number | string; // 64ths of an inch (string for tapered)
  smokeTime: string;
  description: string;
  bestFor: string;
}

export interface WrapperInfo {
  name: string;
  color: string;
  origin: string;
  flavorProfile: string;
  strength: 'mild' | 'mild-medium' | 'medium' | 'medium-full' | 'full';
  description: string;
}

export interface EducationTopic {
  id: string;
  title: string;
  icon: string;
  summary: string;
  content: string;
}

// Vitola (Size) Reference
export const VITOLA_SIZES: VitolaSizeInfo[] = [
  {
    name: 'Petit Corona',
    length: '4.5"',
    ringGauge: 42,
    smokeTime: '20-30 min',
    description: 'A shorter smoke perfect for quick breaks',
    bestFor: 'Quick smoke, beginners, lunch breaks',
  },
  {
    name: 'Robusto',
    length: '5"',
    ringGauge: 50,
    smokeTime: '45-60 min',
    description: 'The most popular size - balanced and satisfying',
    bestFor: 'After dinner, everyday smoke, most versatile',
  },
  {
    name: 'Corona',
    length: '5.5"',
    ringGauge: 42,
    smokeTime: '30-45 min',
    description: 'Classic shape favored by purists',
    bestFor: 'Traditional experience, developing palate',
  },
  {
    name: 'Toro',
    length: '6"',
    ringGauge: 50,
    smokeTime: '60-75 min',
    description: 'Extended robusto for longer enjoyment',
    bestFor: 'Evening relaxation, social gatherings',
  },
  {
    name: 'Churchill',
    length: '7"',
    ringGauge: 48,
    smokeTime: '90-120 min',
    description: 'Named after Winston Churchill - a grand format',
    bestFor: 'Special occasions, weekend afternoons',
  },
  {
    name: 'Gordo/60 Ring',
    length: '6"',
    ringGauge: 60,
    smokeTime: '75-90 min',
    description: 'Thick ring gauge for complex, cool smoke',
    bestFor: 'Experienced smokers, cooler draw preference',
  },
  {
    name: 'Lancero',
    length: '7.5"',
    ringGauge: 38,
    smokeTime: '60-90 min',
    description: 'Long and thin - showcases the wrapper flavor',
    bestFor: 'Connoisseurs, wrapper appreciation',
  },
  {
    name: 'Figurado/Torpedo',
    length: '6"',
    ringGauge: '52 (tapered)',
    smokeTime: '60-75 min',
    description: 'Tapered head concentrates flavors',
    bestFor: 'Experienced smokers seeking complexity',
  },
];

// Wrapper Types
export const WRAPPER_TYPES: WrapperInfo[] = [
  {
    name: 'Connecticut Shade',
    color: 'Light tan/golden',
    origin: 'Connecticut River Valley, USA',
    flavorProfile: 'Creamy, mild, slightly sweet, cedar notes',
    strength: 'mild',
    description: 'Grown under shade cloth to produce delicate, light-colored leaves. The go-to for beginners and those preferring milder smokes.',
  },
  {
    name: 'Connecticut Broadleaf',
    color: 'Dark brown to black',
    origin: 'Connecticut, USA',
    flavorProfile: 'Earthy, sweet, chocolate, coffee',
    strength: 'medium-full',
    description: 'Sun-grown cousin of the shade wrapper. Thick, oily leaves with rich sweetness. Popular for maduro wrappers.',
  },
  {
    name: 'Habano',
    color: 'Medium to dark brown',
    origin: 'Cuba (seed), Nicaragua, Ecuador',
    flavorProfile: 'Spicy, peppery, leather, earth',
    strength: 'medium-full',
    description: 'Cuban-seed tobacco known for its spice and complexity. The backbone of many Nicaraguan powerhouses.',
  },
  {
    name: 'Corojo',
    color: 'Reddish-brown',
    origin: 'Cuba (seed), Honduras, Nicaragua',
    flavorProfile: 'Spicy, red pepper, cedar, nuts',
    strength: 'medium',
    description: 'Originally a Cuban wrapper, now grown in Central America. Known for its signature spice and oily texture.',
  },
  {
    name: 'Maduro',
    color: 'Very dark brown to black',
    origin: 'Various (process, not origin)',
    flavorProfile: 'Sweet, chocolate, coffee, molasses',
    strength: 'medium-full',
    description: 'Not a leaf type but a fermentation process. "Maduro" means "ripe" in Spanish. Extended fermentation creates sweetness and dark color.',
  },
  {
    name: 'Sumatra',
    color: 'Dark brown, reddish tint',
    origin: 'Indonesia, Ecuador',
    flavorProfile: 'Earthy, woody, mild spice, subtle sweetness',
    strength: 'mild-medium',
    description: 'Thin, silky wrapper with understated complexity. Often used in premium cigars for its refined character.',
  },
  {
    name: 'Cameroon',
    color: 'Medium brown, toothy texture',
    origin: 'Central Africa',
    flavorProfile: 'Sweet, floral, woody, mild pepper',
    strength: 'mild-medium',
    description: 'Distinctive "toothy" texture from Africa. Produces a unique sweet and floral profile rare in other wrappers.',
  },
  {
    name: 'Oscuro',
    color: 'Almost black',
    origin: 'Various (extended process)',
    flavorProfile: 'Very rich, dark chocolate, espresso, bold',
    strength: 'full',
    description: 'The darkest of wrappers, achieved through extended fermentation. Intense and bold - for experienced palates.',
  },
];

// Educational Topics
export const EDUCATION_TOPICS: EducationTopic[] = [
  {
    id: 'ring-gauge',
    title: 'What is Ring Gauge?',
    icon: '📏',
    summary: 'Ring gauge measures a cigar\'s diameter in 64ths of an inch',
    content: `**Ring gauge** is the cigar industry's standard for measuring diameter.

A ring gauge of 50 means the cigar is 50/64 of an inch thick (about 0.78 inches or 20mm).

**Why it matters:**
- **Thicker cigars (52+)**: Burn cooler, draw easier, more complex blends possible
- **Thinner cigars (38-42)**: Showcase wrapper flavor, require more attention, warmer smoke
- **Medium gauges (46-50)**: Best balance of all characteristics

**Quick reference:**
- 38 ring = Very thin (Lancero)
- 42 ring = Classic (Corona)
- 50 ring = Standard (Robusto/Toro)
- 60 ring = Thick (Gordo)`,
  },
  {
    id: 'construction',
    title: 'Cigar Anatomy',
    icon: '🔬',
    summary: 'Wrapper, binder, and filler - the three components',
    content: `Every cigar has three essential components:

**1. Wrapper (outermost leaf)**
- Contributes 60-90% of the cigar's flavor
- Most expensive component
- Must be flawless and visually appealing

**2. Binder (middle layer)**
- Holds the filler together
- Adds secondary flavor notes
- Usually a stronger tobacco than wrapper

**3. Filler (core)**
- Can be "long filler" (whole leaves) or "short filler" (chopped)
- Creates the body and strength
- Often a blend of different tobaccos

**Long Filler vs Short Filler:**
- Premium cigars use long filler (better burn, more complex)
- Machine-made cigars use short filler (economical)`,
  },
  {
    id: 'strength-vs-body',
    title: 'Strength vs Body vs Flavor',
    icon: '💪',
    summary: 'Three different characteristics often confused',
    content: `These terms are commonly confused but mean different things:

**Strength** = Nicotine content
- How "strong" the cigar hits you physically
- Can make you dizzy if too strong on an empty stomach
- Rated: Mild → Medium → Full

**Body** = Weight/density of smoke
- How "thick" or "heavy" the smoke feels
- Like comparing skim milk (light) to cream (full)
- A cigar can be full-bodied but mild in strength

**Flavor** = Taste complexity
- The actual flavor notes you experience
- Can be simple (one-dimensional) or complex (evolving)
- Independent of strength and body

**Example:** A Connecticut-wrapped cigar might be:
- Mild strength (low nicotine)
- Medium body (moderate smoke density)
- Complex flavor (cedar, cream, nuts, subtle spice)`,
  },
  {
    id: 'storage',
    title: 'Storing Cigars',
    icon: '🗄️',
    summary: '70/70 rule: 70°F and 70% humidity',
    content: `Proper storage keeps cigars fresh for years:

**The 70/70 Rule:**
- **70°F** (21°C) temperature
- **70% relative humidity**

**Storage options:**
1. **Humidor**: Best for regular smokers
2. **Tupperdor**: Tupperware + humidity pack (budget option)
3. **Coolidor**: Cooler conversion for large collections

**Humidity control:**
- Boveda packs (most popular, maintenance-free)
- Gel jars (need monthly maintenance)
- Humidification beads (need distilled water)

**Warning signs of bad storage:**
- **Too dry**: Cracking wrapper, harsh smoke
- **Too wet**: Mold, plugged draw, tobacco beetles
- **Temperature swings**: Cause wrapper damage

**Pro tip:** Season a new humidor for 1-2 weeks before adding cigars.`,
  },
  {
    id: 'cutting',
    title: 'Cutting Methods',
    icon: '✂️',
    summary: 'Straight cut, V-cut, and punch - each serves a purpose',
    content: `The cut affects your cigar's draw and flavor:

**Straight Cut (Guillotine)**
- Most common and versatile
- Creates large opening for easy draw
- Works on all cigar shapes
- Best for: Beginners, most situations

**V-Cut (Wedge/Cat's Eye)**
- Creates V-shaped notch
- Concentrates smoke to center of tongue
- Prevents tobacco from entering mouth
- Best for: Flavor-chasers, torpedo shapes

**Punch Cut**
- Creates small circular hole
- Coolest smoke, most restricted draw
- Keeps cap intact (no flaking)
- Best for: Large ring gauges, cooler smoke

**Tips:**
- Cut just above the cap line (shoulder)
- Don't cut too deep - causes unraveling
- Sharp cutters are essential
- Punch won't work on pointed (figurado) cigars`,
  },
  {
    id: 'lighting',
    title: 'Proper Lighting',
    icon: '🔥',
    summary: 'Toast the foot, don\'t burn it',
    content: `A proper light sets up your entire smoking experience:

**What to use:**
- **Butane torch** (most popular, clean burn)
- **Soft flame** (traditional, more finesse needed)
- **Cedar spill** (purist choice, imparts subtle flavor)
- **NEVER** use Zippos, candles, or paper matches (chemical taste)

**The Toasting Method:**
1. Hold flame 1/2 inch from foot (don't touch)
2. Rotate cigar, toasting the edge evenly
3. Once foot glows, take first puff
4. Check for even burn line
5. Touch up any unlit spots

**Common mistakes:**
- Rushing (causes tunneling)
- Flame touching cigar (bitter taste)
- Not rotating (uneven burn)
- Over-puffing while lighting (overheating)

**Pro tip:** "Toast and wait" - toast the foot, let it rest 30 seconds, then light fully.`,
  },
];

// Cigar Finder Quiz Questions
export interface QuizQuestion {
  id: string;
  question: string;
  options: {
    value: string;
    label: string;
    description?: string;
  }[];
}

export const CIGAR_FINDER_QUESTIONS: QuizQuestion[] = [
  {
    id: 'experience',
    question: 'What\'s your experience with cigars?',
    options: [
      { value: 'beginner', label: 'New to cigars', description: 'Just starting out or very occasional' },
      { value: 'intermediate', label: 'Some experience', description: 'Smoked a few, developing preferences' },
      { value: 'experienced', label: 'Regular smoker', description: 'Know what I like, seeking variety' },
      { value: 'aficionado', label: 'Aficionado', description: 'Deep knowledge, seeking the exceptional' },
    ],
  },
  {
    id: 'strength',
    question: 'What strength do you prefer?',
    options: [
      { value: 'mild', label: 'Mild', description: 'Light, smooth, easy-going' },
      { value: 'mild-medium', label: 'Mild-Medium', description: 'Some kick but approachable' },
      { value: 'medium', label: 'Medium', description: 'Balanced strength and flavor' },
      { value: 'medium-full', label: 'Medium-Full', description: 'Bold with complexity' },
      { value: 'full', label: 'Full', description: 'Powerful and intense' },
    ],
  },
  {
    id: 'flavors',
    question: 'What flavors appeal to you? (Select up to 3)',
    options: [
      { value: 'sweet', label: 'Sweet/Chocolate', description: 'Cocoa, caramel, vanilla' },
      { value: 'spicy', label: 'Spicy/Peppery', description: 'Black pepper, red pepper, cinnamon' },
      { value: 'earthy', label: 'Earthy/Woody', description: 'Cedar, leather, soil' },
      { value: 'creamy', label: 'Creamy/Nutty', description: 'Cream, almonds, cashews' },
      { value: 'coffee', label: 'Coffee/Espresso', description: 'Roasted beans, dark roast' },
      { value: 'floral', label: 'Floral/Herbal', description: 'Grass, hay, flowers' },
    ],
  },
  {
    id: 'time',
    question: 'How much time do you have?',
    options: [
      { value: 'short', label: '20-30 minutes', description: 'Quick smoke' },
      { value: 'medium', label: '45-60 minutes', description: 'Standard session' },
      { value: 'long', label: '90+ minutes', description: 'Extended enjoyment' },
    ],
  },
  {
    id: 'budget',
    question: 'What\'s your budget per cigar?',
    options: [
      { value: 'value', label: 'Under $8', description: 'Value-focused' },
      { value: 'moderate', label: '$8-15', description: 'Quality everyday smokes' },
      { value: 'premium', label: '$15-25', description: 'Premium selections' },
      { value: 'luxury', label: '$25+', description: 'Top-shelf, special occasions' },
    ],
  },
  {
    id: 'occasion',
    question: 'What\'s the occasion?',
    options: [
      { value: 'daily', label: 'Everyday smoke', description: 'Regular rotation' },
      { value: 'social', label: 'Social gathering', description: 'With friends or clients' },
      { value: 'celebration', label: 'Celebration', description: 'Birthday, promotion, milestone' },
      { value: 'relaxation', label: 'Relaxation', description: 'Winding down, solo enjoyment' },
    ],
  },
];

// Cigar recommendations based on quiz answers
export interface CigarSuggestion {
  name: string;
  brand: string;
  wrapper: string;
  strength: string;
  price: string;
  description: string;
  matchReason: string;
}

export const generateRecommendations = (answers: Record<string, string | string[]>): CigarSuggestion[] => {
  const suggestions: CigarSuggestion[] = [];
  const strength = answers.strength as string;
  const experience = answers.experience as string;
  const flavors = answers.flavors as string[] || [];
  const budget = answers.budget as string;

  // Beginner recommendations
  if (experience === 'beginner' || strength === 'mild') {
    suggestions.push({
      name: 'Macanudo Cafe',
      brand: 'Macanudo',
      wrapper: 'Connecticut Shade',
      strength: 'Mild',
      price: '$7-10',
      description: 'Creamy, smooth, and approachable. The classic starter cigar.',
      matchReason: 'Perfect for beginners - consistent and forgiving',
    });
    suggestions.push({
      name: 'Ashton Classic',
      brand: 'Ashton',
      wrapper: 'Connecticut Shade',
      strength: 'Mild',
      price: '$12-18',
      description: 'Refined and elegant with notes of cedar and cream.',
      matchReason: 'Premium quality in a mild package',
    });
  }

  // Medium strength seekers
  if (strength === 'medium' || strength === 'mild-medium') {
    suggestions.push({
      name: 'Arturo Fuente Hemingway',
      brand: 'Arturo Fuente',
      wrapper: 'Cameroon',
      strength: 'Mild-Medium',
      price: '$10-15',
      description: 'Sweet, woody, with a unique figurado shape.',
      matchReason: 'Excellent complexity without overwhelming strength',
    });
    suggestions.push({
      name: 'My Father Connecticut',
      brand: 'My Father',
      wrapper: 'Ecuadorian Connecticut',
      strength: 'Medium',
      price: '$9-12',
      description: 'Creamy with surprising depth - cedar, nuts, light pepper.',
      matchReason: 'Modern take on Connecticut with more flavor',
    });
  }

  // Full strength lovers
  if (strength === 'medium-full' || strength === 'full') {
    suggestions.push({
      name: 'Padrón 1964 Anniversary',
      brand: 'Padrón',
      wrapper: 'Nicaraguan Maduro/Natural',
      strength: 'Medium-Full',
      price: '$18-25',
      description: 'Rich cocoa, coffee, and earth. Box-pressed perfection.',
      matchReason: 'Gold standard for full-flavored complexity',
    });
    suggestions.push({
      name: 'Liga Privada No. 9',
      brand: 'Drew Estate',
      wrapper: 'Connecticut Broadleaf Oscuro',
      strength: 'Full',
      price: '$15-20',
      description: 'Dark chocolate, espresso, leather, cream.',
      matchReason: 'The modern classic for bold palates',
    });
  }

  // Flavor-specific additions
  if (flavors.includes('sweet') || flavors.includes('coffee')) {
    suggestions.push({
      name: 'Oliva Serie V Melanio',
      brand: 'Oliva',
      wrapper: 'Ecuadorian Sumatra',
      strength: 'Medium-Full',
      price: '$14-18',
      description: 'Dark chocolate, coffee beans, brown sugar sweetness.',
      matchReason: 'Hits the sweet and coffee notes perfectly',
    });
  }

  if (flavors.includes('spicy')) {
    suggestions.push({
      name: 'EP Carrillo Pledge',
      brand: 'E.P. Carrillo',
      wrapper: 'Nicaraguan Habano',
      strength: 'Medium-Full',
      price: '$10-14',
      description: 'Red pepper kick with leather and earth undertones.',
      matchReason: 'Delivers the spice you crave with balance',
    });
  }

  // Budget considerations
  if (budget === 'value') {
    suggestions.push({
      name: 'Joya de Nicaragua Antaño',
      brand: 'Joya de Nicaragua',
      wrapper: 'Nicaraguan Criollo',
      strength: 'Full',
      price: '$6-8',
      description: 'Powerhouse value - earth, pepper, cocoa.',
      matchReason: 'Best bang for your buck in full-bodied',
    });
  }

  if (budget === 'luxury') {
    suggestions.push({
      name: 'Davidoff Winston Churchill',
      brand: 'Davidoff',
      wrapper: 'Ecuadorian',
      strength: 'Medium',
      price: '$28-35',
      description: 'Sophisticated complexity - leather, spice, cream.',
      matchReason: 'When only the finest will do',
    });
  }

  // Return unique suggestions (max 5)
  const unique = suggestions.filter((s, i, arr) =>
    arr.findIndex(x => x.name === s.name) === i
  );
  return unique.slice(0, 5);
};
